import { navigate } from "gatsby"
import { useLocation } from "@reach/router"

export function useQueryParamString({
    key,
    defaultValue,
}: {
    key: string
    defaultValue: string | null
}) {
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)
    const value = searchParams.get(key) ?? defaultValue

    const setValue = (newValue: string | null) => {
        if (newValue) searchParams.set(key, newValue)
        else searchParams.delete(key)

        void navigate(`?${searchParams.toString()}`)
    }

    return [value, setValue] as const
}

// Currently this only works on objects with all string props.
// We can expand this to work with other prop types if needed.
export function useQueryParamObject<
    ValueType extends Record<string, string | null>,
>(defaultValue: ValueType) {
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)

    const parsedParams = Object.keys(defaultValue).reduce((parsed, key) => {
        const value = searchParams.get(key) ?? defaultValue[key] ?? null

        return { ...parsed, [key]: value }
    }, defaultValue)

    const setValue = (update: Partial<ValueType>) => {
        Object.keys(update).forEach((key) => {
            const value = update[key]

            if (value) searchParams.set(key, value)
            else searchParams.delete(key)
        })

        void navigate(`?${searchParams.toString()}`)
    }

    return [parsedParams, setValue] as const
}
